/**
 * 项目编辑页面 - 编辑项目基本信息
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { projectApi } from '../services/projectApi';
import api from '../services/api';
import { toast } from '../store/toastStore';
import { DOCUMENT_ACCEPT, isSupportedDocumentFile } from '../utils/fileTypes';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';

const ProjectEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(-1);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [allPackages, setAllPackages] = useState<any[]>([]);
  const [selectedPackageIdx, setSelectedPackageIdx] = useState(0);

  const [formData, setFormData] = useState({
    project_name: '',
    project_overview: '',
    project_background: '',
    project_scale: '',
    project_schedule: '',
    project_content: '',
    technical_features: '',
    other_requirements: '',
    tech_requirements: '',
    business_requirements: '',
    price_requirements: '',
    expected_word_count: 100000,
    file_content: '',
  });
  const [documentType, setDocumentType] = useState<'bid' | 'proposal'>('bid');

  useEffect(() => {
    if (id) loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await projectApi.getProject(Number(id));
      const p = response.data;
      setDocumentType(p.document_type || 'bid');
      setFormData({
        project_name: p.project_name || '',
        project_overview: p.project_overview || '',
        project_background: p.project_background || '',
        project_scale: p.project_scale || '',
        project_schedule: p.project_schedule || '',
        project_content: p.project_content || '',
        technical_features: p.technical_features || '',
        other_requirements: p.other_requirements || '',
        tech_requirements: p.tech_requirements || '',
        business_requirements: p.business_requirements || '',
        price_requirements: p.price_requirements || '',
        expected_word_count: p.expected_word_count || 100000,
        file_content: p.file_content || '',
      });
      growAllTextareas();
    } catch (error: any) {
      toast.error('加载项目失败: ' + (error.response?.data?.detail || error.message));
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!isSupportedDocumentFile(f)) { toast.warning(`文件 ${f.name} 格式不支持`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.warning(`文件 ${f.name} 超过 10MB`); continue; }
      newFiles.push(f);
    }
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fillFormWithPackage = (pkg: any) => {
    let mergedOverview = '';
    if (pkg.project_overview) mergedOverview += `【项目基本信息】\n${pkg.project_overview}\n\n`;
    if (pkg.project_background) mergedOverview += `【项目背景和目的】\n${pkg.project_background}\n\n`;
    if (pkg.project_scale) mergedOverview += `【项目规模和预算】\n${pkg.project_scale}\n\n`;
    if (pkg.project_schedule) mergedOverview += `【项目时间安排】\n${pkg.project_schedule}\n\n`;
    if (pkg.project_content) mergedOverview += `【项目要实施的具体内容】\n${pkg.project_content}\n\n`;
    if (pkg.technical_features) mergedOverview += `【主要技术特点】\n${pkg.technical_features}\n\n`;
    if (pkg.other_requirements) mergedOverview += `【其他关键要求】\n${pkg.other_requirements}`;

    if (documentType === 'proposal') {
      if (pkg.non_functional_requirements) mergedOverview += `\n\n【非功能性需求】\n${pkg.non_functional_requirements}`;
      if (pkg.acceptance_criteria) mergedOverview += `\n\n【建设目标与验收标准】\n${pkg.acceptance_criteria}`;
      setFormData(prev => ({
        ...prev,
        project_name: pkg.project_name || prev.project_name,
        project_overview: mergedOverview.trim() || prev.project_overview,
        tech_requirements: prev.tech_requirements,
        business_requirements: typeof pkg.business_requirements === 'object' ? JSON.stringify(pkg.business_requirements) : (pkg.business_requirements || prev.business_requirements),
        price_requirements: prev.price_requirements,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        project_name: pkg.project_name || prev.project_name,
        project_overview: mergedOverview.trim() || prev.project_overview,
        tech_requirements: typeof pkg.tech_requirements === 'object' ? JSON.stringify(pkg.tech_requirements) : (pkg.tech_requirements || prev.tech_requirements),
        business_requirements: typeof pkg.business_requirements === 'object' ? JSON.stringify(pkg.business_requirements) : (pkg.business_requirements || prev.business_requirements),
        price_requirements: typeof pkg.price_requirements === 'object' ? JSON.stringify(pkg.price_requirements) : (pkg.price_requirements || prev.price_requirements),
      }));
    }
  };

  const handleUploadAndAnalyze = async () => {
    if (uploadedFiles.length === 0) { toast.warning('请先选择文件'); return; }
    setUploading(true);
    const contents: string[] = [];
    let failed = false;
    const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
    const token = localStorage.getItem('token');
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      setUploadingIndex(i);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/api/document/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.success) {
          contents.push(`【${file.name}】\n${res.data.file_content}`);
        } else { failed = true; break; }
      } catch { failed = true; break; }
    }
    setUploading(false);
    setUploadingIndex(-1);
    if (failed) return;
    const merged = contents.join('\n\n---\n\n');
    setFormData(prev => ({ ...prev, file_content: merged }));
    await analyzeDocument(merged, API_BASE_URL, token);
  };

  const analyzeDocument = async (fileContent: string, API_BASE_URL: string, token: string | null) => {
    setAnalyzing(true);
    setAnalysisProgress(documentType === 'proposal' ? '正在分析方案内容...' : '正在分析标书内容...');
    for (let retry = 0; retry < 3; retry++) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/document/analyze-project-info`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ file_content: fileContent }),
        });
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        while (true) {
          const { done, value } = await reader!.read();
          if (done) break;
          fullText += decoder.decode(value, { stream: true });
        }
        let cleaned = fullText.trim().replace(/^```json\s*/, '').replace(/\s*```$/, '').trim();
        const jsonStart = cleaned.indexOf('[') !== -1 ? cleaned.indexOf('[') : cleaned.indexOf('{');
        const jsonEnd = cleaned.lastIndexOf(']') > cleaned.lastIndexOf('}') ? cleaned.lastIndexOf(']') : cleaned.lastIndexOf('}');
        if (jsonStart !== -1 && jsonEnd > jsonStart) cleaned = cleaned.substring(jsonStart, jsonEnd + 1);
        const result = JSON.parse(cleaned);
        const packages = Array.isArray(result) ? result : (result.packages || [result]);
        setAllPackages(packages);
        if (packages.length > 0) {
          fillFormWithPackage(packages[0]);
          setSelectedPackageIdx(0);
        }
        setAnalyzing(false);
        return;
      } catch (e) {
        if (retry === 2) { setAnalyzing(false); return; }
        await new Promise(r => setTimeout(r, 1000));
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.project_name.trim()) {
      toast.warning('请输入项目名称');
      return;
    }

    setSaving(true);
    try {
      await projectApi.updateProject(Number(id), formData);
      navigate(`/projects/${id}`);
    } catch (error: any) {
      toast.error('保存失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const autoGrow = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  const growAllTextareas = () => {
    setTimeout(() => {
      document.querySelectorAll('textarea').forEach(el => {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      });
    }, 50);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-2">
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          加载中...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-2">
        <div className="mb-8">
          <PageHeader title="编辑项目信息" />
          <p className="text-foreground">修改项目的基本信息和评分要求</p>
        </div>

        <div className="bg-white rounded-2xl border border-border mb-4 overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            <h2 className="text-sm font-bold text-foreground">{documentType === 'proposal' ? '上传需求文件' : '上传招标文件'}</h2>
          </div>
          <div className="p-6">
            <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center">
              {uploadedFiles.length > 0 && (
                <div className="space-y-2 mb-5">
                  {uploadedFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-accent rounded-xl px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <span className="text-sm text-foreground">{f.name}</span>
                        <span className="text-xs text-muted-foreground">{(f.size/1024).toFixed(1)}KB</span>
                      </div>
                      <button onClick={() => removeFile(i)} disabled={uploading||analyzing} className="text-muted-foreground hover:text-red-500">&times;</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center"><svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg></div>
                <div><label htmlFor="fu" className="cursor-pointer text-sm"><span className="text-primary font-medium hover:underline">点击选择文件</span></label>
                <input id="fu" type="file" className="hidden" accept={DOCUMENT_ACCEPT} multiple onChange={handleFileSelect} disabled={uploading||analyzing} /></div>
                <p className="text-xs text-muted-foreground">支持 PDF、Word、TXT、Markdown 等格式，单文件不超过 10MB</p>
                {uploading ? (
                  <p className="text-sm text-primary">上传中（{uploadingIndex+1}/{uploadedFiles.length}）...</p>
                ) : uploadedFiles.length > 0 && (
                  <button onClick={handleUploadAndAnalyze} disabled={analyzing}
                    className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)] disabled:opacity-50">
                    {analyzing ? '分析中...' : '开始分析'}
                  </button>
                )}
              </div>
            </div>
            {analyzing && <p className="mt-3 text-sm text-primary">{analysisProgress}</p>}
            {allPackages.length >= 2 && (
              <div className="mt-4 p-3 bg-primary/10 rounded-xl border border-primary/20">
                <label className="block text-xs font-medium text-foreground mb-1">{documentType === 'proposal' ? '需求分段' : '投标标段'}</label>
                <select value={selectedPackageIdx} onChange={e=>{setSelectedPackageIdx(+e.target.value);fillFormWithPackage(allPackages[+e.target.value])}}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white">
                  {allPackages.map((pkg,i)=><option key={i} value={i}>{pkg.id} - {pkg.name}（{pkg.desc}）</option>)}
                </select>
              </div>
            )}
            {!analyzing && allPackages.length > 0 && <p className="mt-3 text-xs text-green-600"><CheckCircleIcon className="w-5 h-5 inline-block" /> 已自动分析并填充项目信息</p>}
          </div>
        </div>

      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="space-y-4">
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">项目名称 <span className="text-red-400">*</span></label>
            <input type="text" value={formData.project_name} onChange={e=>setFormData({...formData,project_name:e.target.value})}
              disabled={analyzing} className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground" /></div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">项目概述</label>
            <textarea value={formData.project_overview} onChange={e=>setFormData({...formData,project_overview:e.target.value})} onInput={autoGrow}
              disabled={analyzing} rows={12}
              className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none" /></div>
          <div className="flex items-center gap-2 px-4 py-2 bg-accent rounded-xl -mx-1">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg><span className="text-xs font-bold text-foreground">{documentType === 'proposal' ? '需求分析' : '评分项'}</span></div>
          {documentType === 'proposal' ? (
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">业务需求分析 <span className="text-red-400">*</span></label>
              <textarea value={formData.business_requirements} onChange={e=>setFormData({...formData,business_requirements:e.target.value})} onInput={autoGrow}
                disabled={analyzing} rows={6}
                className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none" /></div>
          ) : (
            <>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">技术评分项 <span className="text-red-400">*</span></label>
              <textarea value={formData.tech_requirements} onChange={e=>setFormData({...formData,tech_requirements:e.target.value})} onInput={autoGrow}
                disabled={analyzing} rows={4}
                className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">商务评分项</label>
              <textarea value={formData.business_requirements} onChange={e=>setFormData({...formData,business_requirements:e.target.value})} onInput={autoGrow}
                disabled={analyzing} rows={3}
                className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none" /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">价格评分项</label>
              <textarea value={formData.price_requirements} onChange={e=>setFormData({...formData,price_requirements:e.target.value})} onInput={autoGrow}
                disabled={analyzing} rows={3}
                className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none" /></div>
            </>
          )}
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">预期字数</label>
            <input type="number" value={formData.expected_word_count} onChange={e=>setFormData({...formData,expected_word_count:+e.target.value})}
              disabled={analyzing} className="w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground" /></div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button onClick={() => navigate(`/projects/${id}`)} className="px-6 py-2.5 bg-background hover:bg-accent text-foreground text-sm font-medium border border-border rounded-xl transition-colors">取消</button>
        <button onClick={handleSubmit} disabled={saving}
          className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)] disabled:opacity-50">
          {saving ? '保存中...' : '保存修改'}
        </button>
      </div>
      </div>
  );
};

export default ProjectEdit;
